<template>
	<div class="fill-height bg-dashboard-premium d-flex flex-column overflow-hidden">
		<!-- ── SECCIÓN 1: FILTROS (SUPERIOR) ── -->
		<div class="w-100">
			<v-card class="elevation-2 bg-white border overflow-hidden mb-3">
				<v-row no-gutters class="px-5 py-4" align="center">
					<v-col cols="12">
						<v-row dense align="end" class="mx-n1">
							<v-col cols="12" sm="6" md="2">
								<s-date v-model="filter.BeginDate" outlined dense hide-details class="premium-input-compact" label="Fecha Inicio"/>
							</v-col>
							<v-col cols="12" sm="6" md="2">
								<s-date v-model="filter.EndDate" outlined dense hide-details class="premium-input-compact" label="Fecha Fin"/>
							</v-col>

							<v-col cols="12" sm="6" md="2">
								<s-select :items="years" outlined hide-details dense v-model="filter.PymYear" class="premium-input-compact" label="Año"/>
							</v-col>
							<v-col cols="12" sm="6" md="3">
								<s-periods v-model="PymWeek" :year="filter.PymYear" :type="1" return-object class="premium-input-compact" label="Semana"/>
							</v-col>

							<v-spacer class="d-none d-md-block"></v-spacer>

							<v-col cols="12" sm="12" md="2">
								<v-btn color="primary" block depressed class="rounded-lg font-weight-black elevation-1" @click="fetchPlates()" :loading="loadingPlates" style="height: 40px; margin-bottom: 2px;">
									<v-icon left size="18">mdi-magnify</v-icon> BUSCAR
								</v-btn>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
			</v-card>
		</div>

		<!-- ── SECCIÓN DE TRABAJO (PANEL DIVIDIDO) ── -->
		<v-row no-gutters class="flex-grow-1 overflow-hidden" style="height: calc(100dvh - 200px);">
			<!-- Panel Izquierdo: Vehículos -->
			<v-col cols="12" md="4" lg="2" class="fill-height">
				<v-card
					class="elevation-4 fill-height d-flex flex-column border overflow-hidden bg-soft-gray">
					<div class="px-2 pt-2 pb-2">
						<div class="d-flex align-center justify-space-between mb-1">
							<div class="d-flex align-center">
								<div class="premium-icon-box primary mr-2" style="padding: 8px;">
									<v-icon color="white" small>mdi-car-multiple</v-icon>
								</div>
								<h3 class="text-subtitle-1 font-weight-black grey--text text--darken-4 mb-0">
									Vehículos</h3>
							</div>
							<v-badge overlap color="primary" v-if="uniquePlates.length > 0"
								:content="uniquePlates.length" offset-x="28" offset-y="8" class="mini-badge"></v-badge>
						</div>
						<v-text-field v-model="searchPlate" placeholder="Filtrar por placa..." dense hide-details
							prepend-inner-icon="mdi-magnify" outlined class="premium-search-refined"></v-text-field>
					</div>

					<div class="flex-grow-1 overflow-y-auto px-2 pb-2 custom-scrollbar" style="min-height: 0;">
						<div v-for="(p, i) in filteredPlates" :key="i"
							:class="['vehicle-card', { 'active': (selectedPlate && selectedPlate.Key === p.Key) }]"
							@click="selectPlate(p)">
							<div class="d-flex align-center w-100">
								<div class="plate-avatar mr-3">
									<v-icon
										:color="(selectedPlate && selectedPlate.Key === p.Key) ? 'white' : 'primary'">mdi-truck-outline</v-icon>
								</div>
								<div class="flex-grow-1">
									<div class="plate-number">{{ p.Plate }}</div>
									<div class="plate-info font-weight-bold" :style="{ color: (selectedPlate && selectedPlate.Key === p.Key) ? 'rgba(255,255,255,0.8)' : '#64748b', fontSize: '11px' }">
										{{ p.DateStr }} | {{ p.TypeTransport }} | {{ p.TotalJabas }} Jbs
									</div>
								</div>
							</div>
						</div>
						<div v-if="filteredPlates.length === 0" class="text-center py-10">
							<v-icon size="48" color="grey lighten-3">mdi-car-off</v-icon>
							<p class="grey--text caption mt-2 px-4">No se encontraron vehículos</p>
						</div>
					</div>
				</v-card>
			</v-col>

			<!-- Panel Derecho: Detalle de Pagos -->
			<v-col cols="12" md="8" lg="10" class="fill-height pl-md-2 mt-4 mt-md-0">
				<v-card
					class="elevation-4 fill-height d-flex flex-column border overflow-hidden bg-dashboard-main">
					
					<!-- Pestañas (Tabs) -->
					<div v-if="selectedPlate" class="bg-white" style="border-bottom: 1px solid #e2e8f0;">
						<v-tabs v-model="activeTab" color="primary" height="48" slider-size="3">
							<v-tab class="font-weight-bold" style="letter-spacing: 0px;">
								<v-icon left small>mdi-cash-check</v-icon> Pagos a Procesar
							</v-tab>
							<v-tab class="font-weight-bold" style="letter-spacing: 0px;">
								<v-icon left small>mdi-list-box-outline</v-icon> Detalle de Registros ({{ selectedPlateItems.length }})
							</v-tab>
						</v-tabs>
					</div>

					<div class="flex-grow-1 overflow-hidden pa-0 h-full">
						<!-- TAB 1: Pagos a Procesar -->
						<div v-if="selectedPlate && activeTab === 0" class="h-full d-flex flex-column">
							<v-data-table
								v-model="selected"
								show-select
								item-key="PtcCode"
								:headers="listHeaders"
								:items="dataDetails"
								:loading="loadingDetails"
								height="calc(100dvh - 350px)"
								fixed-header
								dense
								hide-default-footer
								disable-pagination
								class="modern-grid-light h-full"
							>
								<template v-slot:item.PtcAgreedFreightCost="{ item }">
									<v-text-field
										v-model.number="item.PtcAgreedFreightCost"
										dense
										hide-details
										outlined
										type="number"
										style="max-width: 100px;"
										@input="recalculateRow(item)"
									></v-text-field>
								</template>
								<template v-slot:item.PtcStowageQty="{ item }">
									<v-text-field
										v-model.number="item.PtcStowageQty"
										dense
										hide-details
										outlined
										type="number"
										style="max-width: 80px;"
										@input="recalculateRow(item)"
									></v-text-field>
								</template>
								<template v-slot:item.PtcAvgCostJbs="{ item }">
									<span class="caption font-weight-black blue--text">
										{{ item.PtcAvgCostJbs }}
									</span>
								</template>
								<template v-slot:item.PtcTotalCost="{ item }">
									<span class="caption font-weight-black green--text">
										{{ formatMonedaPE(item.PtcTotalCost) }}
									</span>
								</template>
								<template v-slot:item.PtcFinalCostWithTax="{ item }">
									<span class="caption font-weight-black purple--text text--darken-2">
										{{ formatMonedaPE(item.PtcFinalCostWithTax) }}
									</span>
								</template>
							</v-data-table>

							<footer class="panel-footer bg-white px-4 py-3" style="border-top: 1px solid #e2e8f0; margin-top: auto;">
								<v-row no-gutters align="center" class="w-100">
									<v-col class="d-flex align-center">
										<div class="stat-pill">
											<span class="label">SELECCIONADOS:</span>
											<span class="value">{{ selected.length }}</span>
										</div>
										<div class="stat-pill">
											<span class="label">TOTAL A PROCESAR:</span>
											<span class="value success--text">{{ formatMonedaPE(totalSelectedAmount) }}</span>
										</div>
									</v-col>
									<v-col cols="auto" class="d-flex align-center justify-end" style="gap:4px;">
										<v-btn color="primary" depressed
											class="rounded-lg elevation-4 font-weight-black" @click="processPayment()"
											:disabled="selected.length === 0">
											<v-icon left size="24">mdi-cog-play</v-icon> PROCESAR PAGO
										</v-btn>
									</v-col>
								</v-row>
							</footer>
						</div>

						<!-- TAB 2: Detalle de Registros -->
						<div v-if="selectedPlate && activeTab === 1" class="h-full d-flex flex-column">
							<v-data-table
								:headers="config.headers"
								:items="selectedPlateItems"
								height="calc(100dvh - 300px)"
								fixed-header
								dense
								hide-default-footer
								disable-pagination
								class="modern-grid-light h-full"
							>
								<template v-slot:item.Jabas="{ item }">
									<span class="caption font-weight-black blue--text">
										{{ item.Jabas }}
									</span>
								</template>
								<template v-slot:item.TotalCost="{ item }">
									<span class="caption font-weight-black green--text">
										{{ formatMonedaPE(item.TotalCost) }}
									</span>
								</template>
							</v-data-table>
						</div>

						<!-- ESTADO VACÍO -->
                        <div v-if="!selectedPlate" class="d-flex fill-height justify-center align-center flex-column">
							<v-icon size="80" color="grey lighten-2">mdi-cursor-default-click-outline</v-icon>
							<h2 class="grey--text text--lighten-1 mt-4">Selecciona un vehículo para ver sus pagos</h2>
						</div>
					</div>
				</v-card>
			</v-col>
		</v-row>

		<!-- ── Diálogo de Confirmación ── -->
		<v-dialog v-model="dialogConfirm" persistent max-width="520">
			<v-card class="rounded-xl" elevation="0">
				<v-card-title class="process-modal-header pa-0">
					<div class="header-content px-6 py-4">
						<div class="d-flex align-center">
							<v-icon color="white" size="28" class="mr-3">mdi-cog-play</v-icon>
							<div>
								<div class="modal-title">Confirmar Procesamiento</div>
								<div class="modal-subtitle">
									{{ selected.length }} registro(s) seleccionado(s)
								</div>
							</div>
						</div>
						<v-btn icon dark @click="dialogConfirm = false">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</div>
				</v-card-title>

				<v-card-text class="pt-4 pb-2">
					<v-alert type="info" outlined dense class="rounded-lg">
						Se procesará el pago de <strong>{{ selected.length }} transportista(s)</strong>
						por un total de <strong>{{ formatMonedaPE(totalSelectedAmount) }}</strong>.
					</v-alert>
					<p class="caption grey--text mt-2 mb-0">
						Esta acción registrará el procesamiento de pago para los transportistas seleccionados.
					</p>
				</v-card-text>

				<v-divider />
				<v-card-actions class="pa-4">
					<v-spacer />
					<v-btn text @click="dialogConfirm = false" class="rounded-lg">
						<v-icon left>mdi-close</v-icon> Cancelar
					</v-btn>
					<v-btn
						color="primary"
						depressed
						:loading="processing"
						@click="confirmProcess()"
						class="rounded-lg font-weight-black"
					>
						<v-icon left>mdi-check</v-icon> Confirmar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

	</div>
</template>

<script>
import _sPaymentTransportersCollectService from "@/services/Technicalassistance/TasPaymentTransportersCollectService";
import SPeriods from "@/components/Utils/SPeriods.vue";

export default {
	name: "PaymentTransportersCollect",
	components: { SPeriods },
	data: () => ({
		filter: {
			BeginDate: null,
			EndDate: null,
			PymYear: null,
			PymWeek: null,
			VehPlate: null
		},
		filterDetail: {
			BeginDate: null,
			EndDate: null,
			VehPlate: null,
			TypeTransport: null
		},
		PymWeek: null,
		years: [],
		searchPlate: '',
		selectedPlate: null,
		uniquePlates: [],
		loadingPlates: false,

		selected: [],
		dialogConfirm: false,
		processing: false,
		loadingDetails: false,
		dataDetails: [],
		activeTab: 0,
		selectedPlateItems: [],
		listHeaders: [
			{ text: "Semana",           value: "PtcWeekGuide" },
			{ text: "Placa",            value: "PtcVehiclePlate" },
			{ text: "Tipo Transporte",  value: "PtcTransportType" },
			{ text: "Fecha Guía",       value: "PtcCostDate" },
			{ text: "Costo x Jaba",     value: "PtcAvgCostJbs" },
			{ text: "Cant. Jabas",      value: "PtcTotalJabas" },
			{ text: "Envase",           value: "PtcPackageType" },
			{ text: "Localidad de Costo", value: "PtcCostLocality" },
			{ text: "Costo con IGV Flete", value: "PtcCostWithTax" },
			{ text: "Costo acordado Flete", value: "PtcAgreedFreightCost" },
			{ text: "Adicional", value: "PtcAdditionalField" },
			{ text: "Costo Flete", value: "PtcFreightCost" },
			{ text: "Costo Flete IGV", value: "PtcFreightCostExclTax" },
			{ text: "Costo Flaso Flete", value: "PtcFalseFrightCost" },
			{ text: "Cantidad de Estiba", value: "PtcStowageQty" },
			{ text: "Costo Estiba Acordado", value: "PtcAgreedStowageCost" },
			{ text: "Costo Estiba", value: "PtcStowageCost" },
			{ text: "Total Adicional", value: "PtcTotalAdditional" },
			{ text: "Costo Final con IGV", value: "PtcFinalCostWithTax" },
			{ text: "Costo Final - IGV", value: "PtcFinalCostExclTax" },
			{ text: "RUC Vehiculo", value: "PtcVehicleRuc" },
			{ text: "Razon Social", value: "PtcBusinessName" },
		],
		config: {
			headers: [
				{ text: "Semana", value: "WeekGuide"},
				{ text: "ID Proveedor", value: "FltID"},
				{ text: "Proveedor", value: "FagName"},
				{ text: "Localidad", value: "StrDescription"},
				{ text: "Sector", value: "SstrDescription"},
				{ text: "N° Orden Localidad", value: "LocalityOrder"},
				{ text: "Envase", value: "TypeChargeName"},
				{ text: "Variedad", value: "TypeVarietyName"},
				{ text: "Cantidad Programada", value: "RcfQuantityEstimate"},
				{ text: "Cantidad Real", value: "FrmQuantityReal"},
				{ text: "Placa", value: "RegPlateTransport"},
				{ text: "Capc. Min", value: "CfvCapacityMin"},
				{ text: "Capc. Max", value: "CfvCapacity"},
				{ text: "Cant. Jbs", value: "Jabas"},
				{ text: "Guia de Remisión", value: "RcfNumberReferralGuide"},
				{ text: "Guia de Transportista", value: "RcsNumberFerralGuideDriver"},
				{ text: "Peso Guia", value: "RcfWeightGuide"},
				{ text: "Estado Viaje", value: "StateJourney"},
				{ text: "Destino Final", value: "District"},
				{ text: "Vehiculo a transbordar", value: "ToBeTransferred"},
				{ text: "Fecha de llegada", value: "RcsDateReception"},
				{ text: "Tipo de transporte", value: "TypeTransport"},
				{ text: "Cantidad Jbs Falso Flete", value: "CantJbsFalsoFlete"},
				{ text: "Costo Falso Flete", value: "CostFalseFreight"},
				{ text: "Costo Jbs", value: "CostJbs"},
				{ text: "Costo Con IGV S/", value: "TotalCost", width: "100px"},
				{ text: "Costo Final - IGV", value: ""},
				{ text: "N° Factura", value: ""},
				{ text: "Fecha Factura", value: ""},
				{ text: "Fecha de Costo", value: "CostDate"},
				{ text: "Localidad de Costo", value: "CostLocality"},
				{ text: "Semana Costo", value: "CostWeek"},
				{ text: "Estado Peso", value: "WeightStatus"},
				{ text: "CodCosto", value: "CostCode"},
				{ text: "CodSegui", value: "CodSegui"},
				{ text: "Guia-Extr", value: "ExtractedGuide"},
				{ text: "Guia-R. Modificada", value: "ModifiedReferralGuide"},
				{ text: "Costo Jb x Carga (Sin IGV)", value: "CostJbPerLoadExclTax"},
			],
		},
	}),

	computed: {
		filteredPlates() {
			const q = (this.searchPlate || '').toLowerCase().trim();
			if (!q) return this.uniquePlates;
			return this.uniquePlates.filter(p => (p.Plate || '').toLowerCase().includes(q) || (p.TypeTransport || '').toLowerCase().includes(q));
		},
		totalSelectedAmount() {
			return this.selected.reduce((acc, item) => acc + (item.PtcFinalCostWithTax || item.PtcTotalCost || 0), 0);
		},
	},

	methods: {
		async fetchPlates() {
			this.loadingPlates = true;
			this.selectedPlate = null;
			this.filterDetail.VehPlate = null;
			this.filterDetail.TypeTransport = null;
			try {
				const params = {
					filter: { BeginDate: this.filter.BeginDate, EndDate: this.filter.EndDate, VehPlate: "", TypeTransport: "" },
					draw: 1, start: 0, length: -1,
					order: [{ dir: "asc", column: "" }], searchText: "",
				};
				const response = await _sPaymentTransportersCollectService.pagination(params, this.$fun.getUserID());
				const allData = response.status == 200 ? response.data?.data : [];
				
				const platesMap = new Map();
				allData.forEach(item => {
					if (item.RegPlateTransport && item.RegPlateTransport.trim() !== '') {
						const dateStr = item.CostDate ? item.CostDate.substring(0, 10) : (item.RegDate ? item.RegDate.substring(0, 10) : 'SIN FECHA');
						const typeStr = item.TypeTransport || 'GENERAL';
						const key = `${item.RegPlateTransport}_${dateStr}_${typeStr}`;
						
						if (!platesMap.has(key)) {
							platesMap.set(key, {
								Key: key,
								Plate: item.RegPlateTransport,
								DateStr: dateStr,
								TypeTransport: typeStr,
								Records: 0,
								TotalCost: 0,
								TotalJabas: 0,
								Items: []
							});
						}
						const group = platesMap.get(key);
						group.Records++;
						group.TotalCost += (item.TotalCost || 0);
						group.TotalJabas += (item.Jabas || 0);
						group.Items.push(item);
					}
				});
				this.uniquePlates = Array.from(platesMap.values()).sort((a, b) => a.Plate.localeCompare(b.Plate) || a.DateStr.localeCompare(b.DateStr));
			} catch (error) {
				console.error(error);
				// this.$fun.alert("Error al cargar placas", "error");
			} finally {
				this.loadingPlates = false;
			}
		},
		async selectPlate(group) {
			this.selected = [];
			this.selectedPlate = group;
			this.selectedPlateItems = group.Items || [];
			this.activeTab = 0;
			this.filterDetail.VehPlate = group.Plate;
			this.filterDetail.TypeTransport = group.TypeTransport;
			this.filterDetail.BeginDate = group.DateStr !== 'SIN FECHA' ? group.DateStr : this.filter.BeginDate;
			this.filterDetail.EndDate = group.DateStr !== 'SIN FECHA' ? group.DateStr : this.filter.EndDate;

			await this.fetchDetailsCurrentPlate();
		},
		async fetchDetailsCurrentPlate() {
			if (!this.selectedPlate) return;
			this.loadingDetails = true;
			try {
				const response = await _sPaymentTransportersCollectService.list(this.filterDetail, this.$fun.getUserID());
				let data = response.status === 200 ? response.data : [];
				// Asegurar cálculo inicial
				data.forEach(item => this.recalculateRow(item));
				this.dataDetails = data;
			} catch (error) {
				console.error(error);
			} finally {
				this.loadingDetails = false;
			}
		},
		recalculateRow(item) {
			const agreed = Number(item.PtcAgreedFreightCost) || 0;
			const qty = Number(item.PtcStowageQty) || 0;
			const baseTax = Number(item.PtcCostWithTax) || 0;

			item.PtcFreightCost = agreed > 0 ? agreed : baseTax;
			item.PtcAdditionalField = agreed > baseTax ? agreed - baseTax : 0;
			item.PtcFreightCostExclTax = item.PtcFreightCost / 1.18;
			
			item.PtcAgreedStowageCost = qty > 0 ? 100 : null;
			item.PtcStowageCost = qty * 100;
			
			item.PtcTotalAdditional = item.PtcAdditionalField + item.PtcStowageCost;
			item.PtcFinalCostWithTax = item.PtcFreightCost + item.PtcStowageCost;
			item.PtcFinalCostExclTax = item.PtcFinalCostWithTax / 1.18;
		},
		rowSelected(items) {
			this.selected = [...items];
		},
		processPayment() {
			if (this.selected.length === 0) {
				this.$fun.alert("Seleccione al menos un registro para procesar.", "warning");
				return;
			}
			this.dialogConfirm = true;
		},
		async confirmProcess() {
			this.dialogConfirm = false;
			this.processing = true;
			try {
				let successCount = 0;
				for (const header of this.selected) {
					const headerDateStr = header.PtcCostDate ? header.PtcCostDate.substring(0, 10) : null;
					
					const details = this.selectedPlateItems.filter(d => {
						const dDateStr = d.CostDate ? d.CostDate.substring(0, 10) : (d.RegDate ? d.RegDate.substring(0, 10) : null);
						return d.RegPlateTransport === header.PtcVehiclePlate &&
							   dDateStr === headerDateStr &&
							   (d.TypeTransport || 'GENERAL') === header.PtcTransportType &&
							   d.CostCode === header.PtcCode;
					});

					const payload = {
						PtcWeekGuide: header.PtcWeekGuide,
						PtcVehiclePlate: header.PtcVehiclePlate,
						PtcTransportType: header.PtcTransportType,
						PtcCostDate: header.PtcCostDate,
						PtcCostCode: header.PtcCode,
						PtcAvgCostJbs: header.PtcAvgCostJbs,
						PtcTotalJabas: header.PtcTotalJabas,
						PtcPackageType: header.PtcPackageType,
						PtcCostLocality: header.PtcCostLocality,
						PtcCostWithTax: header.PtcCostWithTax,
						PtcAgreedFreightCost: header.PtcAgreedFreightCost,
						PtcAdditionalField: header.PtcAdditionalField,
						PtcFreightCost: header.PtcFreightCost,
						PtcFreightCostExclTax: header.PtcFreightCostExclTax,
						PtcFalseFrightCost: header.PtcFalseFrightCost,
						PtcStowageQty: header.PtcStowageQty,
						PtcAgreedStowageCost: header.PtcAgreedStowageCost,
						PtcStowageCost: header.PtcStowageCost,
						PtcTotalAdditional: header.PtcTotalAdditional,
						PtcFinalCostWithTax: header.PtcFinalCostWithTax,
						PtcFinalCostExclTax: header.PtcFinalCostExclTax,
						PtcVehicleRuc: header.PtcVehicleRuc,
						PtcBusinessName: header.PtcBusinessName,
						Details: details.map(d => ({
							WeekGuide: d.WeekGuide,
							FltID: d.FltID,
							FagName: d.FagName,
							StrDescription: d.StrDescription,
							SstrDescription: d.SstrDescription,
							LocalityOrder: d.LocalityOrder,
							TypeChargeName: d.TypeChargeName,
							TypeVarietyName: d.TypeVarietyName,
							RcfQuantityEstimate: d.RcfQuantityEstimate,
							FrmQuantityReal: d.FrmQuantityReal,
							RegPlateTransport: d.RegPlateTransport,
							CfvCapacityMin: d.CfvCapacityMin,
							CfvCapacity: d.CfvCapacity,
							Jabas: d.Jabas,
							RcfNumberReferralGuide: d.RcfNumberReferralGuide,
							RcsNumberFerralGuideDriver: d.RcsNumberFerralGuideDriver,
							RcfWeightGuide: d.RcfWeightGuide,
							StateJourney: d.StateJourney,
							District: d.District,
							ToBeTransferred: d.ToBeTransferred,
							RcsDateReception: d.RcsDateReception,
							TypeTransport: d.TypeTransport,
							CantJbsFalsoFlete: d.CantJbsFalsoFlete,
							CostFalseFreight: d.CostFalseFreight,
							CostLocality: d.CostLocality,
							CostDate: d.CostDate,
							CostCode: d.CostCode,
							CostJbs: d.CostJbs,
							TotalCost: d.TotalCost,
							CostWeek: d.CostWeek,
							WeightStatus: d.WeightStatus,
							ModifiedReferralGuide: d.ModifiedReferralGuide,
							ExtractedGuide: d.ExtractedGuide,
							CostJbPerLoadExclTax: d.CostJbPerLoadExclTax
						}))
					};

					const response = await _sPaymentTransportersCollectService.save(payload, this.$fun.getUserID());
					if (response.status === 200 && response.data > 0) {
						successCount++;
					}
				}

				if (successCount > 0) {
					this.$fun.alert(`Se procesaron correctamente ${successCount} registros.`, "success");
					this.selected = [];
					this.fetchPlates(); 
					// Optionally, re-fetch the details for the current plate or clear selection
					this.dataDetails = [];
					this.selectedPlateItems = [];
					this.selectedPlate = null;
				} else {
					this.$fun.alert("No se pudo procesar ningún pago.", "error");
				}

			} catch (error) {
				console.error(error);
				this.$fun.alert("Ocurrió un error al guardar el pago.", "error");
			} finally {
				this.processing = false;
			}
		},
		formatMonedaPE(value) {
			if (value == null || isNaN(value)) return "S/ 0.00";
			return "S/ " + Number(value).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		},
		restartFilter() {
			this.filter.BeginDate = this.$moment().format("YYYY-MM-DD");
			this.filter.EndDate = this.$moment().format("YYYY-MM-DD");
		}
	},

	created() {
		this.years = this.$fun.getTop5LastYears();
		this.filter.PymYear = this.$moment().year();
		this.restartFilter();
	},
	mounted() {
		this.fetchPlates();
	},
	watch: {
		PymWeek(value) {
			this.filter.BeginDate = value ? value.PdsDateBegin : null;
			this.filter.EndDate = value ? value.PdsDateEnd : null;
			this.filter.PymWeek = value ? value.PdsNum : null;
		},
		"filter.PymYear"() {
			this.PymWeek = null;
			this.filter.PymWeek = null;
			this.restartFilter();
		}
	},
};
</script>

<style scoped>
/* ── Estilos Dashboard Premium ── */
.bg-dashboard-premium {
	background-color: #f8fafc;
	font-family: 'Inter', 'Roboto', sans-serif;
}
.bg-dashboard-main {
	background-color: #f8fafc;
}
.bg-soft-gray {
	background-color: #f8fafc;
}
.mini-label {
	display: block;
	font-size: 11px;
	font-weight: 800;
	color: #64748b;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 8px;
	padding-left: 4px;
}
.filter-item-group {
	margin-bottom: 12px;
}
.premium-icon-box {
	background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
	padding: 10px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 12px rgba(30, 41, 59, 0.15);
}
.premium-icon-box.primary {
	background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
	box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
}
.vehicle-card {
	background: white;
	border-radius: 12px;
	padding: 4px;
	margin-bottom: 2px;
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	border: 1px solid #e2e8f0;
	display: flex;
	align-items: center;
}
.vehicle-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
	border-color: #cbd5e1;
}
.vehicle-card.active {
	background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
	border-color: transparent;
	box-shadow: 0 12px 20px -5px rgba(37, 99, 235, 0.3);
}
.vehicle-card.active .plate-number {
	color: white !important;
}
.plate-avatar {
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: #eff6ff;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease;
}
.vehicle-card.active .plate-avatar {
	background: rgba(255, 255, 255, 0.2);
}
.plate-number {
	font-weight: 900;
	color: #1e293b;
	font-size: 0.95rem;
	letter-spacing: 0.02em;
}
.custom-scrollbar::-webkit-scrollbar {
	width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
	background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}
.stat-pill {
	background: #f1f5f9;
	border-radius: 0.625rem;
	padding: 4px 8px;
	margin-right: 8px;
	display: flex;
	align-items: center;
	border: 1px solid #e2e8f0;
}
.stat-pill .label {
	font-size: 10px;
	font-weight: 700;
	color: #64748b;
	margin-right: 6px;
}
.stat-pill .value {
	font-size: 14px;
	font-weight: 900;
	color: #1e293b;
}
.process-modal-header {
	background: linear-gradient(135deg, #1e3a5f 0%, #1565c0 60%, #0d47a1 100%);
	border-radius: 12px 12px 0 0 !important;
}
.header-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}
.modal-title {
	font-size: 1.1rem;
	font-weight: 800;
	color: white;
	letter-spacing: -0.01em;
}
.modal-subtitle {
	font-size: 0.78rem;
	color: rgba(255, 255, 255, 0.75);
	margin-top: 2px;
}
</style>
